import { useApp } from '../../../../app/AppContext.jsx';
import { useCatalog } from '../../context/CatalogContext.jsx';

import { useI18n } from '../../../../shared/i18n/useI18n.js';

import {
	applyUserSortSelection,
	getUserEditableSortLevel,
} from '../../utils/buildInitialSorting.js';

import { VIEW_MODE } 	from '../../utils/viewModes.js';
import { SORT_DIRECTION } from '../../constants/sortParameters.js';
import { SORT_DIRECTION_OPTIONS } from '../../registries/sortRegistry.js';

import { Select } from '../../../../shared/ui/Select/index.js';
import { 
	Button, 
	ButtonGroup } 	from '../../../../shared/ui/Button/index.js';
import {
	ViewCards,
	ViewList,
	ViewTiles } 		from '../../../../shared/ui/icons/index.js';

import styles from './CatalogSortRow.module.scss';


// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Sort + view-mode row.
 *
 * Controls the user-editable (always_apply: false) sort level in sortConfig.
 * `always_apply: true` levels are handled silently by the hook and are not surfaced here.
 *
 * `viewMode` and `onViewModeChange` are owned by `CatalogView` — passed as props
 * because the view toggle is not part of the filter/sort state.
 *
 * @param {object}   props
 * @param {import('../utils/viewModes.js').VIEW_MODE[keyof import('../utils/viewModes.js').VIEW_MODE]} props.viewMode
 * @param {(mode: string) => void} props.onViewModeChange
 */
export function CatalogSortRow({ viewMode, onViewModeChange }) {
	const { isMobile } = useApp();
	const { 
		catalogCapabilities,
		sortConfig, setSortConfig } = useCatalog();

	const { t } = useI18n()

	const sortParamEntries = (catalogCapabilities?.catalogSortingParameters ?? []).map(
		(p) => ({
			value: p.id,
			label: t(p.labelKey),
		})
	)

	const sortDirectionOptions = SORT_DIRECTION_OPTIONS.map(
		(d) => ({
			value: d.value,
			label: t(d.labelKey)
		})
	)
		
	// Find the single user-editable level. CMS always_apply levels stay active,
	// but are not surfaced directly by the UI.
	const userLevel = getUserEditableSortLevel(sortConfig);
	const currentParam = userLevel?.parameter ?? null;
	const currentDir = userLevel?.direction ?? SORT_DIRECTION.ASC;

	// ── Handlers ────────────────────────────────────────────────────────────

	function handleParamChange(val) {
		setSortConfig((prev) => applyUserSortSelection(prev, val, currentDir));
	}

	function handleDirChange(val) {
		setSortConfig((prev) => applyUserSortSelection(prev, currentParam, val));
	}

	// ── Render ───────────────────────────────────────────────────────────────

	return (
		<div className={styles.wrapper}>
			<div className={styles.row}>
				{/* Sort controls */}
				<div className={styles.sortControls}>
					<span className={styles.sortTitle}>
						{t('catalog.sort_by')}:
					</span>

					<Select
						value={currentParam}
						onChange={handleParamChange}
						options={sortParamEntries}
						placeholder="—"
						className={styles.paramSelect}
					/>

					<Select
						value={currentDir}
						onChange={handleDirChange}
						options={sortDirectionOptions}
						className={styles.dirSelect}
					/>
				</div>

				{/* View mode toggle */}
				{ viewMode && (
					<div className={styles.viewControls}>
						<ButtonGroup sliding size="medium" className={styles.viewToggleGroup}>
							<Button
								type="button"
								kind="icon"
								size="medium"
								selected={viewMode === VIEW_MODE.LIST}
								aria-pressed={viewMode === VIEW_MODE.LIST}
								aria-label={t('catalog.view_list')}
								onClick={() => onViewModeChange(VIEW_MODE.LIST)}
							>
								<ViewList />
							</Button>

							{isMobile && <Button
								type="button"
								kind="icon"
								size="medium"
								variant='ghost'
								selected={viewMode === VIEW_MODE.CARDS}
								aria-pressed={viewMode === VIEW_MODE.CARDS}
								aria-label={t('catalog.view_cards')}
								onClick={() => onViewModeChange(VIEW_MODE.CARDS)}
							>
								<ViewCards />
							</Button>}

							<Button
								type="button"
								kind="icon"
								size="medium"
								selected={viewMode === VIEW_MODE.TILES}
								aria-pressed={viewMode === VIEW_MODE.TILES}
								aria-label={t('catalog.view_tiles')}
								onClick={() => onViewModeChange(VIEW_MODE.TILES)}
							>
								<ViewTiles />
							</Button>
						</ButtonGroup>
					</div>
				)}
			</div>
		</div>
	);
}
